'use client';

import type { FC } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Credits } from '../Credits';
import { CaseCardBase } from '../cases/CaseCardBase';
import { UserAvatar } from '../UserAvatar/UserAvatar';
import { useNavigation } from '../../contexts/NavigationContext';
import { useLocalTranslation } from '../../hooks/useLocalTranslation';
import { GiLockedChest } from '../icons';
import type { CaseBattleListItemResponse, CaseBattleHistoryItemResponse } from '../../services/generated/Api';
import translations from './BattleCard.translations';

export type BattleData = CaseBattleListItemResponse | CaseBattleHistoryItemResponse;

interface BattleCardProps {
  battle: BattleData;
  onClick?: () => void;
}

/** Battles above this cost get the rotating aurora ring. */
const PREMIUM_COST_THRESHOLD = 500_000;

export const BattleCard: FC<BattleCardProps> = ({ battle, onClick }) => {
  const { t } = useLocalTranslation(translations);
  const navigation = useNavigation();
  const slots = Array.from({ length: battle.maxPlayers }, (_, i) => battle.participants[i]);
  const isPremium = battle.totalCost >= PREMIUM_COST_THRESHOLD;

  /**
   * Подсказка о прокрутке ленты кейсов. Считаем её по самой ленте, а не по числу раундов:
   * при трёх кейсах она уже не помещается на 360px, а на широком экране те же три влезают,
   * и подсказка на неподвижной ленте была бы такой же неправдой, как её отсутствие.
   * Гасим и когда домотали до конца — иначе она обещает то, чего справа уже нет.
   */
  const stripRef = useRef<HTMLDivElement>(null);
  const [showScrollHint, setShowScrollHint] = useState(false);

  const syncScrollHint = useCallback(() => {
    const el = stripRef.current;
    if (!el) return;
    // Дробные ширины после масштабирования дают остаток в доли пикселя — берём допуск.
    setShowScrollHint(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);

  useEffect(() => {
    syncScrollHint();
    const el = stripRef.current;
    if (!el) return;
    const observer = new ResizeObserver(syncScrollHint);
    observer.observe(el);
    return () => observer.disconnect();
  }, [syncScrollHint, battle.rounds.length]);
  const avatarSize = battle.maxPlayers > 2 ? 's' : 'm';
  const emptySlotSize = avatarSize === 's' ? 'w-8 h-8' : 'w-12 h-12';

  const card = (
    <div className="relative w-full text-start rounded-3xl bg-bg-secondary p-3 flex flex-col gap-2.5 overflow-hidden">
      <div className="relative flex items-center justify-between gap-3 px-1">
        <span className="text-label-primary text-[26px] font-bold tabular-nums leading-none tracking-[-0.5px] truncate">
          <Credits amount={battle.totalCost} iconSize={17} />
        </span>
        <span className="inline-flex items-center gap-1.5 text-label-secondary text-[18px] font-bold tabular-nums shrink-0">
          <GiLockedChest size={18} />
          {battle.rounds.length}
        </span>
      </div>

      <div className="relative">
        <div
          ref={stripRef}
          onScroll={syncScrollHint}
          className="relative flex gap-2 overflow-x-auto overflow-y-hidden [-webkit-overflow-scrolling:touch] -mx-1 px-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        >
          {battle.rounds.map((round) => (
            <CaseCardBase
              key={round.id}
              imageUrl={round.caseImageUrl}
              imagePlaceholder={round.caseImagePlaceholder}
              alt={round.caseName}
              className="w-[112px] h-[112px] rounded-2xl shrink-0"
            />
          ))}
        </div>
        {/* The edge is logical, the gradient is not — Tailwind has no direction-aware
            gradient, so the rtl: variant re-aims it at the mirrored edge. */}
        {showScrollHint && (
          <div className="pointer-events-none absolute inset-y-0 -end-1 w-6 bg-gradient-to-r rtl:bg-gradient-to-l from-transparent to-bg-secondary" />
        )}
      </div>

      <div className="relative flex items-end justify-between gap-3 px-1 pb-2.5">
        <div className="flex items-end gap-2">
          {slots.map((u, i) =>
            u ? (
              <button
                key={i}
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  navigation.navigate('profile', { userId: u.userId });
                }}
                className="shrink-0 active:opacity-70"
              >
                <UserAvatar
                  userId={u.userId}
                  firstName={u.firstName ?? null}
                  level={u.level ?? 1}
                  size={avatarSize}
                />
              </button>
            ) : (
              <div
                key={i}
                className={`${emptySlotSize} rounded-full border border-dashed border-label-quaternary shrink-0`}
              />
            ),
          )}
        </div>
        <button
          type="button"
          onClick={() => onClick?.()}
          className="h-9 px-4 rounded-full bg-accent-blue text-label-primary text-[14px] font-semibold shrink-0 active:opacity-85 transition-opacity"
        >
          {t('open')}
        </button>
      </div>
    </div>
  );

  if (!isPremium) return card;

  return (
    <div className="relative rounded-3xl p-[1.5px] shot-battle-conic-ring shadow-[0_0_28px_-8px_rgba(255,214,0,0.55)]">
      {card}
    </div>
  );
};
